import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  KeyIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import axios from '@utils/axios';
import { PageHeader, ConfirmDialog } from '../components/common';

// The DM510 boxes the old server (my.dryermaster.com) knows, and their move to this cloud. The old server's job sends
// a census once a minute; Apply marks the ticked boxes as waiting, and the old server's update gate names the new
// software to them at their next daily update check. Every box behind one internet address moves with the others,
// because the update check does not say which box is asking. A box registered here that is back on the old software
// (a fresh card) reads as not migrated again and is ticked like any other; one the old server sees while it is
// connected here (another device uses its serial) stays migrated and is ticked by hand only.

const REFRESH_MS = 30000;

const when = (iso) => (iso ? format(new Date(iso), 'MMM d, yyyy HH:mm') : '');
const ago = (iso) => (iso ? formatDistanceToNow(new Date(iso), { addSuffix: true }) : '');
const minutesSince = (iso) => (Date.now() - new Date(iso).getTime()) / 60000;

const StatusCell = ({ row, onCancel, cancelling, locked }) => {
  if (row.status === 'migrated') {
    return (
      <div>
        <span className="font-semibold text-green-700">Migrated</span>
        {row.migratedAt && <p className="text-xs text-gray-500">{when(row.migratedAt)}</p>}
        {row.movedAgainAt && <p className="text-xs text-gray-500">moved again {when(row.movedAgainAt)}</p>}
        {row.onCloud ? (
          <p className="text-xs text-green-700">connected to this cloud</p>
        ) : (
          <p className="text-xs text-amber-700">not connected to this cloud</p>
        )}
      </div>
    );
  }
  if (row.status === 'waiting') {
    return (
      <div>
        <span className="font-semibold text-amber-700">Waiting</span>
        {locked && <p className="text-xs text-amber-700">on hold while Apply is locked</p>}
        {row.again && <p className="text-xs text-gray-500">registered here {when(row.migratedAt)}: moving again</p>}
        <p className="text-xs text-gray-500">applied {when(row.appliedAt)}</p>
        {row.appliedBy && <p className="text-xs text-gray-400">by {row.appliedBy}</p>}
        {row.deliveredAt && <p className="text-xs text-blue-700">software delivered {when(row.deliveredAt)}</p>}
        {row.deliveredAt && row.online && minutesSince(row.deliveredAt) > 15 && (
          <p className="text-xs text-amber-700">
            still on the old server although the software was delivered {ago(row.deliveredAt)}: the switch may have failed;
            the box's install log says why
          </p>
        )}
        {!row.deliveredAt && (
          <button
            type="button"
            onClick={() => onCancel(row)}
            disabled={cancelling}
            className="mt-1 text-xs text-red-600 hover:underline disabled:opacity-50"
          >
            Cancel this farm
          </button>
        )}
      </div>
    );
  }
  return (
    <div>
      <span className="text-gray-600">Not migrated</span>
      {row.backOnOld && (
        <p className="text-xs text-amber-700">
          registered here {when(row.migratedAt)}, now back on the old software: tick to move it again
        </p>
      )}
    </div>
  );
};

const Migration = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [search, setSearch] = useState('');
  const [showOffline, setShowOffline] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const [confirmKey, setConfirmKey] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [newKey, setNewKey] = useState(null);

  const load = useCallback(async () => {
    try {
      const response = await axios.get('/dm510-migration');
      setData(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const rows = data?.rows || [];

  // A tick stays only on boxes that can still be applied
  useEffect(() => {
    setSelected((previous) => {
      const selectable = new Set(rows.filter((r) => r.selectable).map((r) => r.serial));
      const next = new Set([...previous].filter((s) => selectable.has(s)));
      return next.size === previous.size ? previous : next;
    });
  }, [data]);

  const bySerial = useMemo(() => new Map(rows.map((r) => [r.serial, r])), [rows]);

  // Ticking a box ticks every applicable box at its address, and unticking one unticks them all. A box registered
  // here that the old server sees again is ticked by hand only: it never comes along with a neighbour.
  const toggle = (row) => {
    if (!row.selectable) return;
    const farm = [row.serial, ...row.farm].filter((s) => {
      const neighbour = bySerial.get(s);
      return neighbour?.selectable && (s === row.serial || neighbour.status !== 'migrated');
    });
    setSelected((previous) => {
      const next = new Set(previous);
      const on = !previous.has(row.serial);
      farm.forEach((s) => (on ? next.add(s) : next.delete(s)));
      return next;
    });
  };

  const needle = search.trim().toLowerCase();
  const matches = (r) =>
    !needle ||
    [r.serial, r.company, r.contact, r.email, r.mobile, r.phone, r.address]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(needle));

  // Connected boxes, grouped by address (the rows arrive sorted that way): the ones still to move, and migrated ones
  // the old server sees again, which can be ticked to move again. Migrated boxes the old server no longer sees are
  // listed under Migrated. Waiting boxes not connected always show; the rest on request.
  const farms = useMemo(() => {
    const groups = [];
    for (const r of rows) {
      if (!r.online) continue;
      const last = groups[groups.length - 1];
      if (last && last.address === r.address) last.rows.push(r);
      else groups.push({ address: r.address, rows: [r] });
    }
    return groups;
  }, [rows]);
  const waitingAway = rows.filter((r) => !r.online && r.status === 'waiting');
  const migrated = rows.filter((r) => r.status === 'migrated' && !r.online);
  const seenAgain = rows.filter((r) => r.status === 'migrated' && r.online).length;
  const offline = rows.filter((r) => !r.online && r.status === 'not_migrated');
  const counts = {
    migrated: rows.filter((r) => r.status === 'migrated').length,
    waiting: rows.filter((r) => r.status === 'waiting').length,
    notMigrated: rows.filter((r) => r.status === 'not_migrated').length,
  };

  const visibleFarms = farms.filter((f) => f.rows.some(matches));
  const visibleWaitingAway = waitingAway.filter(matches);
  const visibleMigrated = migrated.filter(matches);
  const visibleOffline = showOffline ? offline.filter(matches) : [];
  const nothingShown =
    visibleFarms.length + visibleWaitingAway.length + visibleMigrated.length + visibleOffline.length === 0;

  const census = data?.census;
  // Apply stays greyed out while the cloud keeps it locked (the default until it is deliberately unlocked)
  const locked = data ? data.applyLocked !== false : true;
  const canApply =
    !locked && selected.size > 0 && !data?.stale && census?.releasePresent && census?.envs?.includes(data?.target?.env);
  const selectedFarms = new Set([...selected].map((s) => bySerial.get(s)?.address));
  const selectedAgain = [...selected].filter((s) => {
    const r = bySerial.get(s);
    return r?.status === 'migrated' || r?.backOnOld;
  }).length;

  const apply = async () => {
    setBusy(true);
    try {
      const response = await axios.post('/dm510-migration/apply', { serials: [...selected] });
      setResult(response.data);
      setSelected(new Set());
      await load();
    } catch (err) {
      setResult({ error: err.response?.data?.message || err.message });
    } finally {
      setBusy(false);
      setConfirmApply(false);
    }
  };

  const cancel = async (row) => {
    if (!window.confirm(`Cancel the waiting boxes at ${row.address || 'this farm'}? Boxes that already took the new software carry on.`)) return;
    setBusy(true);
    try {
      const response = await axios.delete(`/dm510-migration/apply/${row.serial}`);
      setResult({ cancelled: response.data.cancelled });
      await load();
    } catch (err) {
      setResult({ error: err.response?.data?.message || err.message });
    } finally {
      setBusy(false);
    }
  };

  const makeKey = async () => {
    setBusy(true);
    try {
      const response = await axios.post('/dm510-migration/link-key');
      setNewKey(response.data.key);
      await load();
    } catch (err) {
      setResult({ error: err.response?.data?.message || err.message });
    } finally {
      setBusy(false);
      setConfirmKey(false);
    }
  };

  const renderRow = (r) => {
    const done = r.status === 'waiting' || (r.status === 'migrated' && !r.selectable);
    return (
      <tr key={r.serial} className={done ? 'bg-gray-50 text-gray-400' : 'hover:bg-blue-50/40'}>
        <td className="px-3 py-2 align-top">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={done || selected.has(r.serial)}
            disabled={!r.selectable}
            title={r.reason || 'Tick to move this farm'}
            onChange={() => toggle(r)}
          />
        </td>
        <td className="px-3 py-2 align-top font-mono font-semibold">{r.serial}</td>
        <td className="px-3 py-2 align-top">
          <p className={done ? '' : 'text-gray-900'}>{r.company || <span className="italic">no company on record</span>}</p>
          {r.contact && <p className="text-xs">{r.contact}</p>}
          {!r.inCustomerTable && <p className="text-xs text-amber-600">not in the old customer table</p>}
        </td>
        <td className="px-3 py-2 align-top text-xs break-all">
          {r.email && <p>{r.email}</p>}
          {r.mobile && <p>{r.mobile}</p>}
          {!r.email && !r.mobile && <p className="italic">no email or mobile</p>}
        </td>
        <td className="px-3 py-2 align-top">{r.access ? <span className="text-green-700">On</span> : <span>Off</span>}</td>
        <td className="px-3 py-2 align-top text-xs">
          {r.online && r.status === 'migrated' ? (
            <>
              <span className="text-amber-700">Seen by the old server again</span>
              <p>{r.address}</p>
              <p className="text-amber-700">while connected to this cloud too: another device at this address uses this serial</p>
            </>
          ) : r.online ? (
            <>
              <span className="text-green-700">Online</span>
              {r.backOnOld && <p className="text-amber-700">on the old software again</p>}
            </>
          ) : (
            <>
              <span>Not connected</span>
              {r.lastBoot && <p title={when(r.lastBoot.at)}>old software last started {ago(r.lastBoot.at)}</p>}
            </>
          )}
        </td>
        <td className="px-3 py-2 align-top text-xs" title={r.lastAsk ? when(r.lastAsk.at) : ''}>
          {r.lastAsk ? (
            <>
              <p>{ago(r.lastAsk.at)}</p>
              {r.lastAsk.answer && <p className="text-gray-400">answered {r.lastAsk.answer}</p>}
            </>
          ) : (
            <span className="italic">none recorded since Oct 11</span>
          )}
        </td>
        <td className="px-3 py-2 align-top text-xs">{r.lastPayment ? format(new Date(r.lastPayment), 'MMM d, yyyy') : ''}</td>
        <td className="px-3 py-2 align-top text-xs">
          <StatusCell row={r} onCancel={cancel} cancelling={busy} locked={locked} />
        </td>
        <td className="px-3 py-2 align-top text-xs break-all">
          {r.account ? (
            <>
              <p className="text-green-700">{r.account.email}</p>
              {r.account.since && <p className="text-gray-500">since {when(r.account.since)}</p>}
            </>
          ) : r.status === 'migrated' ? (
            <span className="italic">none yet: the customer has not been through the move page</span>
          ) : null}
          {!r.account && r.emailHasAccount && (
            <p className="text-blue-700">the email on file already has an app login: the move adds the box to it</p>
          )}
        </td>
      </tr>
    );
  };

  const header = (
    <thead className="bg-gray-100 text-left text-xs uppercase tracking-wide text-gray-600">
      <tr>
        <th className="px-3 py-2 w-8" />
        <th className="px-3 py-2">Serial</th>
        <th className="px-3 py-2">Farm</th>
        <th className="px-3 py-2">Email / mobile</th>
        <th className="px-3 py-2">Access</th>
        <th className="px-3 py-2">Online now</th>
        <th className="px-3 py-2">Last update check</th>
        <th className="px-3 py-2">Last payment</th>
        <th className="px-3 py-2">Status</th>
        <th className="px-3 py-2">Account</th>
      </tr>
    </thead>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-500">
        <ArrowPathIcon className="mr-2 h-6 w-6 animate-spin" /> Loading the migration list...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        subtitle="DM510 boxes on the old server and their move to this cloud. Every box behind one internet address moves together."
        actions={
          <>
            <button
              type="button"
              onClick={load}
              className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <ArrowPathIcon className="h-4 w-4" /> Refresh
            </button>
            <button
              type="button"
              onClick={() => setConfirmApply(true)}
              disabled={!canApply || busy}
              title={locked ? 'Apply is locked on this cloud' : ''}
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-40"
            >
              Apply{selected.size ? ` (${selected.size})` : ''}
            </button>
          </>
        }
      />

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <ExclamationTriangleIcon className="h-5 w-5" /> {error}
        </div>
      )}

      {/* What the page is working from */}
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm">
          <p className="mb-1 font-semibold text-gray-800">Census from the old server</p>
          {census ? (
            <>
              <p>
                as of {when(census.generatedAt)} <span className="text-gray-400">({ago(census.receivedAt)})</span>
              </p>
              <p className="text-gray-600">{census.connected} boxes connected now</p>
              <p className="text-gray-600">
                customer table: {data.customers ? `${data.customers.count} boxes, ${when(data.customers.receivedAt)}` : 'not received yet'}
              </p>
            </>
          ) : (
            <p className="text-gray-600">None yet. The job on the old server sends one every minute once it is installed.</p>
          )}
          {data?.stale && census && (
            <p className="mt-2 flex items-center gap-1 text-amber-700">
              <ExclamationTriangleIcon className="h-4 w-4" /> {data.stale}
            </p>
          )}
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm">
          <p className="mb-1 font-semibold text-gray-800">Where Apply sends them</p>
          {locked && (
            <p className="mb-1 flex items-center gap-1 font-semibold text-amber-700">
              <LockClosedIcon className="h-4 w-4" /> Apply is locked on this cloud: no box can be moved from this page
            </p>
          )}
          <p>
            this cloud ({data?.target?.env === 'test' ? 'the test cloud' : 'production'}):{' '}
            {census?.envs?.includes(data?.target?.env) ? (
              <span className="text-green-700">the old server has its settings</span>
            ) : (
              <span className="text-amber-700">the old server has no "{data?.target?.env}" settings yet</span>
            )}
          </p>
          <p>
            software on offer: {census?.release || '?'}{' '}
            {census?.releasePresent ? (
              <span className="text-green-700">(on the old server)</span>
            ) : (
              <span className="text-amber-700">(not on the old server)</span>
            )}
          </p>
          <p className="mt-1 text-xs text-gray-500">Each box moves at its next daily update check, within 24 hours of Apply.</p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm">
          <p className="mb-1 flex items-center gap-1 font-semibold text-gray-800">
            <KeyIcon className="h-4 w-4" /> Census link key
          </p>
          {data?.link?.set ? (
            <p className="text-gray-600">
              made {when(data.link.createdAt)} by {data.link.createdBy}
            </p>
          ) : (
            <p className="text-gray-600">Not made yet. The old server's job needs one.</p>
          )}
          <button
            type="button"
            onClick={() => setConfirmKey(true)}
            disabled={busy}
            className="mt-2 rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {data?.link?.set ? 'Make a new key' : 'Make the key'}
          </button>
        </div>
      </div>

      {newKey && (
        <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 text-sm">
          <p className="font-semibold text-amber-900">The new census link key, shown this once:</p>
          <p className="mt-2 select-all break-all rounded bg-white px-3 py-2 font-mono text-gray-900">{newKey}</p>
          <p className="mt-2 text-amber-900">
            It goes into the census job's settings on the old server. It is not kept here, only a fingerprint of it; a
            lost key means making a new one.
          </p>
          <button type="button" onClick={() => setNewKey(null)} className="mt-2 text-xs text-amber-900 underline">
            I have stored it, hide it
          </button>
        </div>
      )}

      {result && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          {result.error && <p className="text-red-700">{result.error}</p>}
          {result.applied?.length > 0 && <p>Applied: {result.applied.join(', ')}</p>}
          {result.added?.length > 0 && <p>Added because they share an address: {result.added.join(', ')}</p>}
          {result.again?.length > 0 && <p>Moved again: {result.again.join(', ')}</p>}
          {result.applied && result.applied.length === 0 && !result.refused?.length && <p>Nothing was applied.</p>}
          {result.refused?.map((r) => (
            <p key={r.serial} className="text-amber-800">
              {r.serial}: {r.reason}
            </p>
          ))}
          {result.cancelled && <p>Cancelled: {result.cancelled.join(', ')}</p>}
          <button type="button" onClick={() => setResult(null)} className="mt-1 text-xs underline">
            Dismiss
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Serial, farm, contact, email, address"
            className="w-80 rounded-lg border border-gray-300 py-2 pl-8 pr-3 text-sm"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={showOffline} onChange={(e) => setShowOffline(e.target.checked)} />
          Show the boxes not connected ({offline.length})
        </label>
        <span className="text-sm text-gray-500">
          migrated {counts.migrated} · waiting {counts.waiting} · not migrated {counts.notMigrated}
        </span>
        {selected.size > 0 && (
          <span className="text-sm font-semibold text-primary-700">
            {selected.size} ticked at {selectedFarms.size} {selectedFarms.size === 1 ? 'address' : 'addresses'}
          </span>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          {header}
          <tbody className="divide-y divide-gray-100">
            {visibleFarms.map((farm) => (
              <React.Fragment key={farm.address}>
                <tr className="bg-primary-50/60">
                  <td colSpan={10} className="px-3 py-1.5 text-xs font-semibold text-primary-900">
                    {farm.address} · {farm.rows.length} {farm.rows.length === 1 ? 'box' : 'boxes'} behind this address
                  </td>
                </tr>
                {farm.rows.filter(matches).map(renderRow)}
              </React.Fragment>
            ))}
            {visibleWaitingAway.length > 0 && (
              <>
                <tr className="bg-amber-50">
                  <td colSpan={10} className="px-3 py-1.5 text-xs font-semibold text-amber-900">
                    Waiting, not connected to the old server right now: still named at their last known address
                  </td>
                </tr>
                {visibleWaitingAway.map(renderRow)}
              </>
            )}
            {visibleMigrated.length > 0 && (
              <>
                <tr className="bg-green-50">
                  <td colSpan={10} className="px-3 py-1.5 text-xs font-semibold text-green-900">
                    Migrated: registered on this cloud ({counts.migrated})
                    {seenAgain > 0 && `, ${seenAgain} of them seen by the old server again while connected here (another device uses the serial), listed above`}
                  </td>
                </tr>
                {visibleMigrated.map(renderRow)}
              </>
            )}
            {visibleOffline.length > 0 && (
              <>
                <tr className="bg-gray-100">
                  <td colSpan={10} className="px-3 py-1.5 text-xs font-semibold text-gray-700">
                    Not connected to the old server: they can move when they connect again
                  </td>
                </tr>
                {visibleOffline.map(renderRow)}
              </>
            )}
            {nothingShown && (
              <tr>
                <td colSpan={10} className="px-3 py-10 text-center text-gray-500">
                  <InformationCircleIcon className="mx-auto mb-2 h-6 w-6" />
                  {rows.length ? 'Nothing matches.' : 'No boxes yet: the list fills from the old server\'s census.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={confirmApply}
        title="Move these boxes to the new cloud?"
        confirmLabel="Apply"
        busy={busy}
        onCancel={() => setConfirmApply(false)}
        onConfirm={apply}
        message={
          <>
            <p>
              {selected.size} {selected.size === 1 ? 'box' : 'boxes'} at {selectedFarms.size}{' '}
              {selectedFarms.size === 1 ? 'address' : 'addresses'}: {[...selected].join(', ')}.
            </p>
            {selectedAgain > 0 && (
              <p>
                {selectedAgain} of them {selectedAgain === 1 ? 'is' : 'are'} registered here already and will be moved again.
              </p>
            )}
            <p>
              The old server names the new software ({census?.release}) to them; each box replaces its old software at its
              next daily update check and then connects here.
            </p>
          </>
        }
      />

      <ConfirmDialog
        open={confirmKey}
        title={data?.link?.set ? 'Make a new census link key?' : 'Make the census link key?'}
        confirmLabel="Make the key"
        busy={busy}
        onCancel={() => setConfirmKey(false)}
        onConfirm={makeKey}
        message={
          data?.link?.set ? (
            <p>The current key stops working at once: the old server's census stops arriving until its settings carry the new key.</p>
          ) : (
            <p>The key is shown once, for the census job's settings on the old server.</p>
          )
        }
      />
    </div>
  );
};

export default Migration;
